import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";

import { Button } from "@fronterahealth/frontera-ui-components";

interface TemplateSaveFooterProps {
  isDirty: boolean;
  isSaving?: boolean;
  onSave: () => void;
  onDiscard: () => void;
  saveLabel?: string;
  discardLabel?: string;
  saveDisabled?: boolean;
  /** When provided the footer shows "Last saved …" at rest instead of disappearing. */
  lastSavedAt?: Date;
  /** Always render the discard button even when not dirty (e.g. edit mode with no changes yet). */
  alwaysShowDiscard?: boolean;
  /** Optional destructive action rendered on the left side of the footer. */
  dangerAction?: { label: string; onClick: () => void; disabled?: boolean };
  /**
   * Optional promotion action rendered on the right side when not dirty.
   * Used by the prompt test bed to expose "Open PR" once the draft is
   * saved — a natural iterate → save → promote flow.
   */
  promoteAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    isPending?: boolean;
  };
}

export function TemplateSaveFooter({
  isDirty,
  isSaving = false,
  onSave,
  onDiscard,
  saveLabel = "Save Changes",
  discardLabel = "Discard",
  saveDisabled = false,
  lastSavedAt,
  alwaysShowDiscard = false,
  dangerAction,
  promoteAction,
}: TemplateSaveFooterProps) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center gap-4 border-t border-border-medium bg-bg-surface px-6 py-3 shadow-lg">
      {dangerAction && (
        <button
          type="button"
          onClick={dangerAction.onClick}
          disabled={dangerAction.disabled}
          className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {dangerAction.label}
        </button>
      )}
      {isDirty && (
        <span className="flex items-center gap-1.5 text-sm font-medium text-amber-600">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
          Unsaved changes
        </span>
      )}
      <div className="flex items-center gap-3 ml-auto">
        {isDirty ? (
          <>
            <Button text={discardLabel} appearance="secondary" onClick={onDiscard} disabled={isSaving} />
            <Button
              text={isSaving ? "Saving…" : saveLabel}
              appearance="primary"
              onClick={onSave}
              disabled={isSaving || saveDisabled}
            />
          </>
        ) : (
          <>
            {alwaysShowDiscard && (
              <Button text={discardLabel} appearance="secondary" onClick={onDiscard} disabled={isSaving} />
            )}
            {lastSavedAt && (
              <span className="text-sm text-text-tertiary">Last saved {format(lastSavedAt, "MMM d 'at' h:mm a")}</span>
            )}
            {promoteAction && (
              <Button
                text={promoteAction.isPending ? "Opening PR…" : promoteAction.label}
                appearance="primary"
                onClick={promoteAction.onClick}
                disabled={promoteAction.disabled || promoteAction.isPending}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
